import { useState } from "react";
import { Mail, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* ================= Block 1 — Hero ================= */

const ContactHero = () => (
  <section data-testid="contact-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="mx-auto max-w-[62rem] text-center">
        <div className="flex items-center justify-center gap-3" aria-hidden="true">
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
            Contact WFII
          </span>
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
        </div>
        <h1 className="font-serif-editorial mx-auto mt-8 max-w-4xl text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Get in Touch With World Financial Innovation Institute
        </h1>
        <p className="mx-auto mt-8 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
          Have a question about WFII programs, membership, partnerships,
          resources,{" "}
          <br className="hidden lg:block" aria-hidden="true" />
          or corporate education?
        </p>
        <p className="mx-auto mt-4 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg">
          Contact our team and we will direct your inquiry to the right person.
        </p>
        <div className="mt-9 inline-flex flex-col items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
            General Inquiries
          </span>
          <a
            href="mailto:info@wfi-institute.com"
            className="group inline-flex items-center gap-2.5 text-lg font-semibold text-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md sm:text-xl"
            data-testid="contact-general-email-link"
          >
            <Mail
              className="h-5 w-5 text-[hsl(var(--accent))]"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            info@wfi-institute.com
          </a>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 2 — How we can help ================= */

const HELP_TOPICS = [
  {
    n: "01",
    title: "Programs and Certification",
    body: "Questions about WFII programs, certificates, learning formats, member pricing, or upcoming courses.",
  },
  {
    n: "02",
    title: "Membership",
    body: "Questions about individual membership, corporate membership, member access, resources, and benefits.",
  },
  {
    n: "03",
    title: "Partnerships",
    body: "For organizations interested in sponsoring programs, contributing speakers, supporting research, joining working groups, or becoming a WFII partner.",
  },
  {
    n: "04",
    title: "Corporate Training",
    body: (<>For companies, accounting firms, fintech teams, finance departments, and professional organizations interested{" "}<br className="hidden lg:block" aria-hidden="true" />in tailored digital finance education.</>),
  },
  {
    n: "05",
    title: "Media, Speaking and Events",
    body: (<>For webinar invitations, speaker requests, interviews,{" "}<br className="hidden lg:block" aria-hidden="true" />panels, and contributor opportunities.</>),
  },
];

const HowWeCanHelp = () => (
  <section data-testid="contact-help-section">
    <Container className="">
      <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-center lg:text-5xl">
        How We Can Help
      </h2>
      <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {HELP_TOPICS.map((t) => (
          <div
            key={t.n}
            className="group border-t border-[hsl(var(--accent))] pt-6"
            data-testid={`contact-help-item-${t.n}`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium tracking-[0.08em] text-muted-foreground">
                {t.n}
              </span>
              <span
                className="h-2 w-2 rounded-[2px] bg-[hsl(var(--accent))]/0 transition-colors group-hover:bg-[hsl(var(--accent))]/80"
                aria-hidden="true"
              />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-foreground">{t.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

/* ================= Block 3 — Contact form ================= */

const INQUIRY_TYPES = [
  "Programs and Certification",
  "Membership",
  "Partnerships",
  "Corporate Training",
  "Media",
  "General Inquiry",
];

const ContactForm = () => {
  const [inquiryType, setInquiryType] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inquiryType) {
      toast.error("Please select an inquiry type.", {
        description: "Choose the topic that best matches your question.",
      });
      return;
    }
    const form = e.target;
    const data = new FormData(form);
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          inquiry_type: inquiryType,
          full_name: data.get("fullName"),
          organization: data.get("organization") || "",
          email: data.get("email"),
          message: data.get("message"),
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      form.reset();
      setInquiryType("");
      toast.success("Your inquiry has been received.", {
        description: "We review inquiries and respond as soon as possible.",
      });
    } catch (err) {
      toast.error("Something went wrong while sending your inquiry.", {
        description: "Please try again in a moment or email us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="contact-form-section">
      <Container className="">
        <div className="grid gap-10 lg:grid-cols-[440px_1fr] lg:gap-16">
          {/* Left — heading + note */}
          <div>
            <div className="lg:sticky lg:top-24">
              <span className="block h-[3px] w-12 bg-[hsl(var(--accent))]" aria-hidden="true" />
              <h2 className="mt-6 text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
                Contact Form
              </h2>
              <p className="mt-8 text-sm leading-relaxed text-muted-foreground sm:text-base">
                *We review inquiries and respond as soon as possible. For
                partnership, corporate training, or speaking requests, please
                include a short description of your organization and the type
                of collaboration you are interested in.
              </p>
            </div>
          </div>

          {/* Right — form card */}
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] sm:p-10"
            data-testid="contact-form"
          >
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="inquiry-type" className="text-sm font-medium text-foreground">
                  Inquiry Type
                </Label>
                <Select value={inquiryType} onValueChange={setInquiryType}>
                  <SelectTrigger
                    id="inquiry-type"
                    className="h-11 bg-background"
                    data-testid="contact-form-inquiry-type-trigger"
                  >
                    <SelectValue placeholder="Select an inquiry type" />
                  </SelectTrigger>
                  <SelectContent>
                    {INQUIRY_TYPES.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        data-testid={`contact-form-inquiry-option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="full-name" className="text-sm font-medium text-foreground">
                    Full Name
                  </Label>
                  <Input
                    id="full-name"
                    name="fullName"
                    required
                    autoComplete="name"
                    placeholder="Your full name"
                    className="h-11 bg-background"
                    data-testid="contact-form-name-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="organization" className="text-sm font-medium text-foreground">
                    Organization
                  </Label>
                  <Input
                    id="organization"
                    name="organization"
                    autoComplete="organization"
                    placeholder="Company or organization"
                    className="h-11 bg-background"
                    data-testid="contact-form-organization-input"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@company.com"
                  className="h-11 bg-background"
                  data-testid="contact-form-email-input"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="message" className="text-sm font-medium text-foreground">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  placeholder="How can we help?"
                  className="min-h-[140px] resize-y bg-background"
                  data-testid="contact-form-message-textarea"
                />
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98] disabled:opacity-60"
                  data-testid="contact-form-submit-button"
                >
                  {submitting ? "Submitting..." : "Submit Inquiry"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Container>
    </section>
  );
};

/* ================= Block 4 — Institutional note ================= */

const InstituteNote = () => (
  <section data-testid="contact-institute-note-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="mx-auto max-w-3xl text-center">
        <div className="flex items-center justify-center gap-3" aria-hidden="true">
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
        </div>
        <p className="mx-auto mt-8 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg">
          World Financial Innovation Institute is a Canada-based education and
          research institute for professionals working with modern finance,
          digital money, payments, accounting workflows, compliance, and
          financial infrastructure.
        </p>
      </div>
    </Container>
  </section>
);

/* ================= Page ================= */

export default function ContactPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[60px] lg:space-y-[120px]">
          <ContactHero />
          <HowWeCanHelp />
          <ContactForm />
          <InstituteNote />
        </main>
        <Footer />
        <Toaster position="bottom-right" />
      </div>
    </div>
  );
}
